import { DOCUMENT } from "@angular/common";
import { Directive, output, inject, ElementRef, HostListener, Component, input, signal, model, LOCALE_ID, computed } from "@angular/core";

// It's good practice to define an interface for your Language object
interface Language {
    code: string;
    name: string;
    icon?: string;
}

@Directive({
    selector: "[appClickOutside]",
    standalone: true, // Directives can also be standalone
})
export class ClickOutsideDirective {
    clickOutside = output();
    private elementRef = inject(ElementRef);

    @HostListener("document:click", ["$event.target"])
    public onClick(targetElement: HTMLElement): void {
        const clickedInside = this.elementRef.nativeElement.contains(targetElement);
        if (!clickedInside) {
            this.clickOutside.emit();
        }
    }
}

@Component({
    selector: "in2-language-selector",
    standalone: true,
    imports: [ClickOutsideDirective],
    template: `
        <div class="container" appClickOutside (clickOutside)="isOpen.set(false)">
            <div>
                <button type="button" class="button" id="menu-button" aria-expanded="true" aria-haspopup="true" (click)="toggleDropdown()">
                    @if (selectedLanguage()?.icon) {
                        <span class="icon">{{ selectedLanguage().icon }}</span>
                    }
                    <span>{{ selectedLanguage()?.name }}</span>
                    <svg class="arrow" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                            fill-rule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 01.02-1.06z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>
            </div>

            @if (isOpen()) {
                <div class="menu" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                    <div class="items-wrapper" role="none">
                        @for (lang of languages(); track lang.code) {
                            <a
                                href="#"
                                class="item"
                                [class.is-selected]="lang.code === selectedLanguage()?.code"
                                role="menuitem"
                                tabindex="-1"
                                (click)="selectLanguage(lang); $event.preventDefault()"
                            >
                                @if (lang.icon) {
                                    <span class="item-icon">{{ lang.icon }}</span>
                                }
                                <span>{{ lang.name }}</span>
                            </a>
                        }
                    </div>
                </div>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .container {
                position: relative;
                display: inline-block;
                text-align: start;
            }

            .button {
                display: inline-flex;
                justify-content: center;
                align-items: center;
                gap: 0.375rem;
                border-radius: 0.375rem;
                background-color: #fff;
                padding: 0.25rem;
                font-size: 0.875rem;
                font-weight: 600;
                color: #1f2937;
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                border: 1px solid #d1d5db;
                cursor: pointer;
                white-space: nowrap;

                &:hover {
                    background-color: #f9fafb;
                }

                &:focus {
                    outline: 2px solid transparent;
                    outline-offset: 2px;
                    box-shadow:
                        0 0 0 2px #fff,
                        0 0 0 4px #3b82f6;
                }
            }

            .icon {
                font-size: 1.25rem;
                line-height: 1;
            }

            .arrow {
                width: 1.25rem;
                height: 1.25rem;
                margin-inline-start: 0.25rem;
                color: #9ca3af;
            }

            .menu {
                position: absolute;
                inset-inline-end: 0;
                top: auto;
                bottom: 100%;
                margin-bottom: 0.5rem;
                z-index: 10;
                width: 10rem;
                border-radius: 0.375rem;
                background-color: #fff;
                box-shadow:
                    0 10px 15px -3px rgba(0, 0, 0, 0.1),
                    0 4px 6px -2px rgba(0, 0, 0, 0.05);
                border: 1px solid rgba(0, 0, 0, 0.05);
                transform-origin: bottom end;
                box-sizing: border-box;
            }

            .items-wrapper {
                padding-top: 0.25rem;
                padding-bottom: 0.25rem;
            }

            .item {
                display: flex;
                padding: 0.5rem 1rem;
                font-size: 0.875rem;
                color: #374151;
                text-decoration: none;
                white-space: nowrap;
                cursor: pointer;
                flex-direction: row;
                flex-wrap: nowrap;
                align-items: center;

                &:hover {
                    background-color: #f3f4f6;
                    color: #1f2937;
                }

                &.is-selected {
                    background-color: #f3f4f6;
                    font-weight: 600;
                }
            }

            .item-icon {
                font-size: 1.25rem;
                margin-inline-end: 0.5rem;
            }
        `,
    ],
})
export class LanguageSelectorComponent {
    private readonly doc: Document = inject(DOCUMENT);
    languages = input<Language[]>([
        { code: "en", name: "English", icon: "🇬🇧" },
        { code: "ar", name: "العربيّة", icon: "🇸🇦" },
    ]);

    readonly isOpen = signal(false);
    value = model<string>(inject(LOCALE_ID));
    selectedLanguage = computed(() => this.languages().find((lang) => lang.code === this.value()));


    toggleDropdown(): void {
        this.isOpen.set(!this.isOpen());
    }

    selectLanguage(language: Language): void {
        this.value.set(language.code);
        this.isOpen.set(false);
        const langPrefix = language.code !== "en" ? `/${language.code}/` : "/";
        this.doc.location.href = langPrefix;
    }
}
